// Generated by tutorials/record.cjs from tutorials/articles/h31-nutrition-assessments.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h31 --text-only` (wording) or
// `node tutorials/record.cjs h31` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'nutrition-assessments',
  catalogId: 'h31',
  title: 'Nutrition assessments',
  summary: 'Start a nutrition assessment for a resident, fill in each section while the page works out BMI and needs, then finish, sign and print it.',
  audience: 'Dietitians and dietary managers who complete nutrition assessments',
  area: 'clinical',
  learn: [
    'Find the assessments for your facility or for one resident',
    'Start a new assessment and pick its type',
    'Fill in the sections, and what the page works out for you',
    'Finish and sign the assessment',
    'Print it, and who can change it',
  ],
  keywords: [
    'assessment',
    'assessments',
    'nutrition assessment',
    'nutritional assessment',
    'quarterly',
    'annual',
    'admission',
    'significant change',
    'bmi',
    'ibw',
    'ideal body weight',
    'weight change',
    'calorie needs',
    'protein needs',
    'fluid needs',
    'harris-benedict',
    'sign',
    'signature',
    'electronically sign',
    'print assessment',
    'new blank',
    'blank assessment',
    'follow up',
  ],
  steps: [
    {
      id: 'list',
      part: 'Find assessments',
      heading: 'See every assessment',
      text: 'Under Clinical, select Assessments. You see your facility\'s assessments, newest first: the resident, the date, the type, whether it is completed and signed, who created it and when it is due for follow up.\n\nType a resident\'s name, a type or a report name in Search assessments to narrow the list. Click a row to open that assessment, or select Edit to change it.',
      media: {
        image: {
          src: '/help/nutrition-assessments/list.webp',
          width: 1280,
          height: 720,
        },
        clip: {
          src: '/help/nutrition-assessments/list.mp4',
          width: 1280,
          height: 720,
          durationSec: 4.3,
        },
        alt: 'The Assessments list narrowed to Ellison: two assessments for Marjorie Ellison, a Quarterly Review and an Admission Assessment, both completed and signed.',
      },
    },
    {
      id: 'resident',
      part: 'Find assessments',
      heading: 'Or open them from the resident',
      text: 'Each resident\'s assessments are also in their record: open the resident and select Assessments on the left. The chips at the top say how many there are and the date of the latest one.\n\nThis is where you start a new assessment, and where you select one to open, edit, sign or print.',
      media: {
        image: {
          src: '/help/nutrition-assessments/resident.webp',
          width: 1280,
          height: 720,
        },
        clip: {
          src: '/help/nutrition-assessments/resident.mp4',
          width: 1280,
          height: 720,
          durationSec: 6,
        },
        alt: 'Marjorie Ellison\'s Assessments section with New, New Blank, Electronically Sign, Open, Edit, Print, Print with All Options and More actions, above her Quarterly Review and Admission Assessment.',
      },
    },
    {
      id: 'type',
      part: 'Start a new assessment',
      heading: 'Select New and pick the type',
      text: 'Select New. Pick the type of assessment, such as Quarterly Review, and select Next.\n\nThe type decides which sections the assessment has.',
      media: {
        image: {
          src: '/help/nutrition-assessments/type.webp',
          width: 824,
          height: 362,
        },
        clip: {
          src: '/help/nutrition-assessments/type.mp4',
          width: 824,
          height: 362,
          durationSec: 2,
        },
        alt: 'The Assessment type choice: Admission Assessment, Quarterly Review (selected), Annual Review and Significant Change, with Cancel and Next.',
      },
    },
    {
      id: 'report-header',
      part: 'Start a new assessment',
      heading: 'Move through the sections',
      text: 'The sections are listed on the left. Select one to fill it in; you can go back to any section before you finish.\n\nReport Header comes first. The report name and today\'s date are filled in. Follow Up is set for you a number of days after the date (90 here) and moves when you change the date. Add any comments.\n\nThe line under the toolbar says whether finishing will also update the resident\'s record. It does when the assessment date is in the last 30 days and no other assessment is dated later.',
      media: {
        image: {
          src: '/help/nutrition-assessments/report-header.webp',
          width: 1280,
          height: 720,
        },
        clip: {
          src: '/help/nutrition-assessments/report-header.mp4',
          width: 1280,
          height: 720,
          durationSec: 3,
        },
        alt: 'New Assessment: the sections Report Header, Resident Info, Frame/Height/Weight, Calorie Needs, Protein Needs, Fluid Needs and Signature on the left, and Report Header open with the report name, today\'s date and a follow-up date 90 days later.',
      },
    },
    {
      id: 'resident-info',
      part: 'Start a new assessment',
      heading: 'Check the resident\'s details',
      text: 'Resident Info starts with what the resident\'s record says: name, unit, room, physician, diagnosis and the rest. Correct anything that has changed.',
      media: {
        image: {
          src: '/help/nutrition-assessments/resident-info.webp',
          width: 1280,
          height: 878,
        },
        clip: {
          src: '/help/nutrition-assessments/resident-info.mp4',
          width: 1280,
          height: 878,
          durationSec: 2.5,
        },
        alt: 'Resident Info for Marjorie Ellison: unit Birch, room 114, bed B, birth date 10/19/1945, physician Feld, Marcus MD, advance directive FULL CODE and the diagnosis.',
      },
    },
    {
      id: 'weight',
      part: 'Start a new assessment',
      heading: 'Enter the height and weight',
      text: 'On Frame/Height/Weight, check the height and frame, and enter today\'s weight in Current.\n\nThe page works out the BMI and IBW % as you type. IBW low and high are worked out from the height, frame and gender; if you type your own, they are kept, and Auto Calculate IBW puts the calculated values back.\n\nThe Weight Change table compares the current weight with the resident\'s weights about 30, 90 and 180 days ago, in pounds and percent. Then check whether the change is significant or planned.',
      media: {
        image: {
          src: '/help/nutrition-assessments/weight.webp',
          width: 1280,
          height: 878,
        },
        clip: {
          src: '/help/nutrition-assessments/weight.mp4',
          width: 1280,
          height: 878,
          durationSec: 6,
        },
        alt: 'Frame/Height/Weight with height 5 ft 5 in, Current 168, BMI 27.95, IBW % 133.86, IBW 113 to 138, and the Weight Change table: -3 lbs (-1.75 %) at 30 days, -6 (-3.45 %) at 90 and -8 (-4.55 %) at 180.',
      },
    },
    {
      id: 'calories',
      part: 'Start a new assessment',
      heading: 'Calorie needs',
      text: 'On Calorie Needs, pick the equation and the weight to use: ABW (the actual weight), IBW or ADJ (the adjusted weight). For Harris - Benedict, enter the activity and injury factors; the guidelines under them help you choose.\n\nThe calories line shows the result and how it was worked out.',
      media: {
        image: {
          src: '/help/nutrition-assessments/calories.webp',
          width: 1280,
          height: 878,
        },
        alt: 'Calorie Needs with Harris - Benedict, Weight To Use ABW, Activity Factor 1.2 and Injury Factor 1, and the line 1573 Calories (TEE) = 1311 (BEE) x 1.2 AF x 1 IF, above the activity and injury factor guidelines.',
      },
    },
    {
      id: 'protein',
      part: 'Start a new assessment',
      heading: 'Protein needs',
      text: 'On Protein Needs, pick the weight to use and the resident\'s condition. The page shows the grams of protein a day, low and high, and the factors behind them. Renal Failure also asks whether the resident is on dialysis.',
      media: {
        image: {
          src: '/help/nutrition-assessments/protein.webp',
          width: 1280,
          height: 878,
        },
        clip: {
          src: '/help/nutrition-assessments/protein.mp4',
          width: 1280,
          height: 878,
          durationSec: 4.7,
        },
        alt: 'Protein Needs with Weight To Use IBW and Normal Nutrition picked: Low 57 kg x 0.8 Protein Factor = 46 g and High 57 kg x 1.0 Protein Factor = 57 g.',
      },
    },
    {
      id: 'fluid',
      part: 'Start a new assessment',
      heading: 'Fluid needs',
      text: 'On Fluid Needs, pick the weight to use and the condition that fits, such as an adult over 75. The page shows the fluid a day, low and high. For Other, type your own low and high amounts per kilogram.',
      media: {
        image: {
          src: '/help/nutrition-assessments/fluid.webp',
          width: 1280,
          height: 878,
        },
        clip: {
          src: '/help/nutrition-assessments/fluid.mp4',
          width: 1280,
          height: 878,
          durationSec: 4.8,
        },
        alt: 'Fluid Needs with Res with CHF or renal disease or adult >75 picked: Low 76 kg x 25 cc/kg (BW) = 1905 cc and High 76 kg x 30 cc/kg (BW) = 2286 cc.',
      },
    },
    {
      id: 'signature',
      part: 'Start a new assessment',
      heading: 'The signature',
      text: 'Signature sets how the signature prints, and the name and title that go with it. Add 2nd Signature makes room for a second person.\n\nYou can sign now with Sign, which puts your signature and today\'s date on the assessment when you finish. Or leave it and sign later, as shown below.',
      media: {
        image: {
          src: '/help/nutrition-assessments/signature.webp',
          width: 1280,
          height: 878,
        },
        alt: 'The Signature section: Signature Type Signature & Date, Last Page Only checked, Name Dana Morgan, Title Dietary Manager, the Sign button and a preview of your signature.',
      },
    },
    {
      id: 'finish',
      part: 'Start a new assessment',
      heading: 'Select Finish',
      text: 'Select Finish to save the assessment. If something is missing, such as a condition on Protein Needs, a box lists it with a link to its section; fix it and select Finish again.\n\nThe saved assessment then opens, ready to read. Edit opens it again for changes.',
      media: {
        image: {
          src: '/help/nutrition-assessments/finish.webp',
          width: 1280,
          height: 720,
        },
        clip: {
          src: '/help/nutrition-assessments/finish.mp4',
          width: 1280,
          height: 720,
          durationSec: 2.8,
        },
        alt: 'The saved Quarterly Review dated today, with Edit and Electronically Sign on the toolbar, the message Assessment saved, and its Report Header.',
      },
    },
    {
      id: 'sign',
      part: 'Sign and print it',
      heading: 'Sign the assessment',
      text: 'To sign a saved assessment, open it and select Electronically Sign. Dietech adds your signature and today\'s date. On the resident\'s Assessments section, select the assessment first.\n\nThe button then reads (Signed). A signature cannot be cleared or signed again. A blank form has no date and cannot be signed.\n\nNo signature on file yet? On the resident\'s Assessments section, Electronically Sign offers Draw your signature: draw it once, it is kept with your user profile, and the assessment is signed.',
      media: {
        image: {
          src: '/help/nutrition-assessments/sign.webp',
          width: 1280,
          height: 720,
        },
        clip: {
          src: '/help/nutrition-assessments/sign.mp4',
          width: 1280,
          height: 720,
          durationSec: 4.4,
        },
        alt: 'The saved assessment\'s Signature section with the signature of Dana Morgan and today\'s date, and (Signed) on the toolbar.',
      },
    },
    {
      id: 'print',
      part: 'Sign and print it',
      heading: 'Print it',
      text: 'On the resident\'s Assessments section, select the assessment and then Print. Print with All Options prints every part of it. The report opens when it is ready, and it is also kept in your Print Queue.\n\nTo remove an assessment, select it, then More actions and Delete.',
      media: {
        image: {
          src: '/help/nutrition-assessments/print.webp',
          width: 1280,
          height: 720,
        },
        alt: 'Marjorie Ellison\'s Assessments section with today\'s signed Quarterly Review selected, and the Print button.',
      },
    },
    {
      id: 'view-only',
      part: 'Sign and print it',
      heading: 'When you can only look',
      text: 'If your security level lets you view assessments but not change them, the Assessments pages say so at the top, and New and Edit are not offered. You can still open and read every assessment. Ask your administrator if you need more.',
      textOnly: true,
    },
  ],
  tips: [
    'Need a paper form to fill in by hand? On the resident\'s Assessments section, select New Blank and pick the type. Then select the blank form and Print. It prints with every option.',
    'You can move between sections in any order. Nothing is saved until you select Finish.',
    'If you leave before you finish, Dietech asks first. If the browser closed, open the assessment again on the same computer and select Restore draft to pick up where you left off.',
  ],
  notes: [
    'The sections you see, and the fields in them, depend on the assessment type. Your facility sets up its types, so yours may look different from the pictures here.',
    'The calculated values (BMI, IBW %, the weight change table and the needs) are worked out again each time you open the assessment. They are shown, not typed in.',
    'Starting an assessment needs a Nutritional Assessment security level above 2, changing one above 1, signing 2 or more, and deleting above 3. If you can only look, ask your administrator.',
  ],
  related: ['plan-of-care', 'entering-weights', 'finding-a-resident', 'running-a-report'],
  recorded: '2026-11-03',
};

export default article;
